import React, {Fragment, useEffect, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {
    InfoIcon,
    InfoRow,
    InfoRowForUpload,
    LeftSection,
    SectionIcon,
    SectionTab,
    Wrapper
} from "./AccountingRow.styles";
import CurrentAmountIcon from "../../../../public/images/current-amount-icon.svg";
import AddCreditIcon from "../../../../public/images/add-credit-icon.svg";
import {Alert, Form, Input, Modal, Spin, message} from "antd";
import {useStore} from "../../../../hooks/useStore";
import useIsFocused from "../../../../hooks/useIsFocused";
import {useFetch} from "../../../../hooks/useFetch";
import {useTranslation} from "react-i18next";
import {calculateKDV, calculateKDVAddedAmount, getKDVPercentage} from "../../../../utils/kdvHelper";
import {formatTurkishNumber, parseTurkishNumber} from "../../../../utils/moneyHelper";
import {DocumentModal} from "../../../documents/documentModal";
import {
    ContractDetails,
    InfoHeader,
    InfoItem
} from "components/layouts/customerLayout/components/customerAccountingDetailInfo/CustomerAccountingDetailInfo.styles";
import {CaretRightOutlined} from "@ant-design/icons";
import ContratCalculator from "../../../../public/images/contract-calculator.svg";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {CustomerCodAggrementBlock} from "components/layouts/customerLayout/components/customerCodAggrementBlock/CustomerCodAggrementBlock";
import moment from "moment";
import {stripEmpty} from "../../../../utils/stripEmpty";
import {formatNumberString} from "../../../../utils/helpers";
import {useObserver} from "mobx-react-lite";
const RELOAD_INTERVAL = 1000 * 10; //10 seconds

const getDefaultTimeRange = () => {
    const now = moment();
    const currentHour = now.hour();
    const stores = useStore();

    // Case 1: Between 00:00-06:00
    if (currentHour >= 0 && currentHour < 6) {
        const startDate = moment()
            .subtract(1, "day")
            .set({hour: 6, minute: 0, second: 0, millisecond: 0});
        const endDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
        return [startDate, endDate];
    }

    // Case 2: Between 06:00-23:59
    if (currentHour >= 6 && currentHour <= 23) {
        const startDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
        const endDate = moment();
        return [startDate, endDate];
    }

    // Case 3: Default case (should not normally occur)
    const startDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
    const endDate = moment()
        .add(1, "day")
        .set({hour: 6, minute: 0, second: 0, millisecond: 0});
    return [startDate, endDate];
};

export const AccountingRow = () => {
    const history = useHistory();
    const location = useLocation();
    const stores = useStore();
    const isFocused = useIsFocused();
    const {t} = useTranslation();
    const noWidthForRightSection =
        location.pathname === "/musteri" || location.pathname === "/musteri/hesabim";
    const [recall, setRecall] = useState(true);
    const [isCreditModalActive, setIsCreditModalActive] = useState(false);
    const [newCreditAmount, setNewCreditAmount] = useState(0);
    const [displayValue, setDisplayValue] = useState("");
    const recallRef = useRef(recall);
    const [documentModalVisible, setDocumentModalVisible] = useState(false);
    const [documentUrl, setDocumentUrl] = useState(null);
    const [isNeedForApproval, setIsNeedForApproval] = useState(null);
    const [isApproved, setIsApproved] = useState(null);
    const [contractId, setContractId] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [summary, setSummary] = useState(null);
    const isCodPage = location.pathname === "/musteri/kapida-odeme";
    recallRef.current = recall;
    const subCompanyId = stores?.meStore?.subcompanies?.[0]?._id;
    const isAccountingPage = location.pathname === "/musteri/muhasebe";
    const [filters, setFilters] = useState({
        startDate: getDefaultTimeRange()[0],
        endDate: getDefaultTimeRange()[1]
    });
    const [isPaymentLinkLoading, setIsPaymentLinkLoading] = useState(false);
    const [isLoadingBalance, setIsLoadingBalance] = useState(false);

    const [loadOnce, setLoadOnce] = useState(false);

    const [creditStatsLoading, creditStatsData, _, __] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.userStore.getUserCreditStats.bind(null, {}),
        dependencies: [recall],
        initialCall: false,
        callbackFunc: () => setLoadOnce(true)
    });

    const [subCompanyPaymentContractsLoading, subCompanyPaymentContracts, refetch] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyPaymentContracts.bind(null, {
            subCompanyId
        })
    });

    const subcompanyCredit = stores?.userStore?.currentCredit;

    // Fetch payment summary for COD commission rate using manual fetch

    useEffect(() => {
        const fetchPaymentSummary = async () => {
            if (!subCompanyId) return;

            try {
                const [startDate, endDate] = getDefaultTimeRange();
                const filters = {
                    startDate,
                    endDate
                };

                const raw = {
                    ...filters,
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                    startTime: startDate.format("HH:mm"),
                    endTime: endDate.format("HH:mm")
                };

                const payload = stripEmpty(raw);
                const summaryRes = await stores.codPaymentStore.getPaymentSummary(payload);
                setSummary(summaryRes || {});
            } catch (error) {
                console.error("Error fetching payment summary:", error);
                setSummary({});
            }
        };

        fetchPaymentSummary();
    }, [filters, subCompanyId, stores.codPaymentStore]);

    useEffect(() => {
        const activeContractPrices = subCompanyPaymentContracts?.data?.data?.filter(
            contract => contract?.endDate === null
        );
        setContracts(activeContractPrices);
    }, [subCompanyPaymentContracts]);

    useEffect(() => {
        setTimeout(() => {
            setRecall(!recallRef.current);
        }, 200);
    }, []);

    useEffect(() => {
        if (isFocused) {
            let timeout = null;

            timeout = setTimeout(() => {
                setRecall(!recallRef.current);
            }, RELOAD_INTERVAL);

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    const createPaymentLink = async () => {
        setIsPaymentLinkLoading(true);

        try {
            const response = await stores.companyStore.createPaymentLink({
                subCompanyId,
                amount: newCreditAmount
            });

            if (response?.paymentUrl) {
                window.open(response?.paymentUrl, "_blank", "norefferer");
            } else if (Boolean(response)) {
                message.error("Ödeme linki oluşturulurken bir hata oluştu.");
            }
        } catch (error) {
            console.log(error);
            message.error("Ödeme linki oluşturulurken bir hata oluştu.");
        } finally {
            setIsPaymentLinkLoading(false);
        }
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get("status");

        if (status === "success") {
            message.success("Ödemeniz başarıyla gerçekleşti. Bakiyeniz güncellendi.");
        } else if (status === "fail") {
            message.error("Ödemeniz gerçekleştirilemedi. Lütfen tekrar deneyin.");
        }
    }, []);
    // todo: same logic appears in CustomerAccountingRow, change there when you change this.
    const checkUnapprovedDocuments = async ({subCompanyId}) => {
        const contractName = "serviceAgreement";
        const params = {subCompanyId, contractName: contractName};
        const contract = await stores.contractStore.createAndShowContract(params);
        if (contract?.success === false) return false;
        if (
            contract?.isExistingContract === undefined ||
            (contract?.isExistingContract === true &&
                contract?.isNeedForApproval &&
                !contract?.isApproved)
        ) {
            setDocumentUrl(contract.contractPath);
            setIsNeedForApproval(true);
            setIsApproved(false);
            setDocumentModalVisible(true);

            await stores.contractStore.changeApprovalStatus({
                contractId: contract.contractId,
                isShown: true
            });
            setContractId(contract.contractId);
            return true;
        }
        return false;
    };

    const handleUpdateCredit = async ({subCompanyId}) => {
        setIsLoadingBalance(true);

        const unapprovedExists = await checkUnapprovedDocuments({subCompanyId: subCompanyId});
        if (!unapprovedExists) {
            setIsCreditModalActive(true);
        }

        setIsLoadingBalance(false);
    };

    const handleOnCancelDocumentModal = async () => {
        if (documentModalVisible && documentUrl) {
            const payload = {
                contractId: contractId,
                isApproved: false
            };
            await stores.contractStore.changeApprovalStatus(payload);
        }
        setDocumentModalVisible(false);
        setDocumentUrl(null);
        setContractId(null);
        setRecall(!recallRef.current);
    };

    const handleOnApprove = async () => {
        const payload = {
            contractId: contractId,
            isApproved: true
        };
        await stores.contractStore.changeApprovalStatus(payload);
        setDocumentModalVisible(false);
        setIsCreditModalActive(true);
        setDocumentUrl(null);
        setContractId(null);
    };

    const handleInputChange = e => {
        const value = e.target.value;

        if (!/^[\d.,]*$/.test(value)) {
            return;
        }

        const formattedValue = formatTurkishNumber(value);
        const numericValue = parseTurkishNumber(formattedValue);

        setDisplayValue(formattedValue);
        setNewCreditAmount(numericValue);
    };

    useEffect(() => {
        if (isCreditModalActive) {
            setDisplayValue("");
            setNewCreditAmount(0);
        }
    }, [isCreditModalActive]);

    const shouldShowCodBlock = summary?.codCommissionRate != null;

    return useObserver(() => (
        <Fragment>
            {isLoadingBalance && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(255, 255, 255, 0.7)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999
                    }}
                >
                    <Spin size="large" />
                </div>
            )}
            <Wrapper $noWidth={noWidthForRightSection}>
                {/* <SectionTab>
                    <SectionIcon src={AccountingSectionTabIcon} />
                    Muhasebe
                </SectionTab> */}
                <LeftSection>
                    <InfoItem>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                gap: "16px"
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    cursor: "pointer"
                                }}
                                onClick={() => {
                                    history.push("/musteri/muhasebe");
                                }}
                            >
                                <InfoHeader>
                                    <img src={CurrentAmountIcon} alt="current amount icon" />
                                    <b>Bakiyem:</b>
                                </InfoHeader>
                                <div style={{fontSize: "16px", fontWeight: "bold", color: "#333"}}>
                                    {subcompanyCredit ? formatNumberString(subcompanyCredit) : 0} ₺
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500"
                                }}
                                onClick={() => handleUpdateCredit({subCompanyId})}
                            >
                                <img
                                    src={AddCreditIcon}
                                    alt="add credit icon"
                                    style={{width: "16px", height: "16px"}}
                                />
                                Bakiye Yükle
                            </div>
                        </div>
                    </InfoItem>
                </LeftSection>
                {isAccountingPage && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px"
                        }}
                    >
                        <InfoItem>
                            <InfoHeader>
                                <img src={ContratCalculator} alt="contract calculator icon" />
                                <b>Güncel Sözleşme</b>
                            </InfoHeader>
                        </InfoItem>
                        <InfoItem
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                flexDirection: "row"
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {contracts?.map(contract => (
                                    <div
                                        key={contract?._id}
                                        style={{fontSize: "16px", color: "#389e0d"}}
                                    >
                                        <b style={{marginRight: "8px"}}>
                                            {contract?.feeType === "PAKET" && "Paket Ücreti:"}
                                            {contract?.feeType === "KM" && "Mesafe Ücreti:"}
                                        </b>
                                        <b style={{fontWeight: "600", color: "#389e0d"}}>
                                            {contract?.feeType === "PAKET" &&
                                                `${contract?.clientAmount}₺`}
                                            {contract?.feeType === "KM" &&
                                                `${contract?.clientAmount}₺`}
                                        </b>
                                    </div>
                                ))}
                            </div>
                            <ContractDetails>
                                <CustomButton
                                    href={"/musteri/sozlesme"}
                                    className="all-contracts-btn"
                                    style={{marginRight: "0px"}}
                                >
                                    Tüm Sözleşmeler
                                    <CaretRightOutlined />
                                </CustomButton>
                            </ContractDetails>
                        </InfoItem>
                    </div>
                )}
                {isCodPage && (
                    <CustomerCodAggrementBlock
                        isBackoffice={false}
                        codCommissionRate={summary?.codCommissionRate}
                        externalRecall={recall}
                    />
                )}
            </Wrapper>
            <DocumentModal
                visibility={documentModalVisible && documentUrl !== null}
                onCancel={handleOnCancelDocumentModal}
                documentUrl={documentUrl}
                title="Onay Bekleyen Sözleşme"
                getContainer={false}
                onApprove={handleOnApprove}
                isNeedForApproval={isNeedForApproval}
                isApproved={isApproved}
                contractId={contractId}
                isApproveButtonDisabled={false}
            />
            <Modal
                visible={isCreditModalActive}
                onCancel={() => setIsCreditModalActive(false)}
                centered
                title={"Kredi Yükle"}
                okText={"Tamam"}
                onOk={createPaymentLink}
                cancelText={t("modal.cancelButton2", {ns: "common"})}
                okButtonProps={{loading: isPaymentLinkLoading, disabled: !newCreditAmount}}
            >
                <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                    <Form.Item label={"Yüklemek istediğiniz bakiye tutarını giriniz (₺)"}>
                        <Input
                            style={{width: "100%"}}
                            placeholder={"Örn: 1.000,50"}
                            value={displayValue}
                            onChange={handleInputChange}
                            addonAfter="₺"
                            maxLength={15}
                        />
                        <p style={{marginBottom: "0px", marginTop: "16px"}}>
                            KDV (%{getKDVPercentage()})
                        </p>
                        <p>
                            {calculateKDV(newCreditAmount).toLocaleString("tr-TR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}{" "}
                            ₺
                        </p>

                        <p style={{marginBottom: "0px"}}>
                            <b>KDV Dahil Tutar</b>
                        </p>
                        <p style={{marginBottom: "0px"}}>
                            <b>
                                {calculateKDVAddedAmount(newCreditAmount).toLocaleString("tr-TR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}{" "}
                                ₺
                            </b>
                        </p>
                    </Form.Item>
                </Form>
            </Modal>
        </Fragment>
    ));
};
